// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseLabel, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IGeographicRevenueReport, IQueryParamListExport } from '~/utils/interface/reports';
import { BACK_TO_PREVIOUS_PAGE, colorsRevenueReport, DEFAULT_CURRENT_PAGE } from '~/utils/constants/common';
import { IHeaderCellType } from '~/utils/interface/common';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { exportDataReport, getListGeographyRevenueReport } from '~/thunks/reports/reportsThunk';
// Styles, images, icons
import styles from './GeographicRevenueReport.module.scss';
import { icons } from '~/assets';
import { ReportTypeEnum } from '~/utils/enum/common';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IGeographicRevenueReport>[] => {
  return [
    {
      id: 'postalCode',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_zip_code')}</Box>
        </Stack>
      ),
      render: (record: IGeographicRevenueReport) => (
        <Stack direction='row' spacing={3} alignItems='center'>
          <Box width={12} height={12} borderRadius={6} bgcolor={record.color} />
          <Typography className={cx('zipCode')}>{record?.postalCode}</Typography>
        </Stack>
      ),
    },
    {
      id: 'totalRevenue',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_total_revenue')}</Box>
        </Stack>
      ),
      render: (record: IGeographicRevenueReport) => {
        return (
          <Typography className={cx('textValue')}>
            {formatCurrencyUSD({ value: record?.totalRevenue, showDecimal: true })}
          </Typography>
        );
      },
    },
  ];
};

const GeographicRevenueReport = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [geographicRevenueReport, setGeographicRevenueReport] = useState<IGeographicRevenueReport[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({});
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListSubRentalsReport();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListSubRentalsReport = () => {
    setIsLoading(true);

    dispatch(getListGeographyRevenueReport(paramsObject))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        const coloredData = data.map((item, index) => ({
          ...item,
          color: colorsRevenueReport[index % colorsRevenueReport.length],
        }));

        setGeographicRevenueReport(coloredData);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.GEOGRAPHIC_REVENUE,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };
  //#endregion Handle Function

  return (
    <Stack id='geographicRevenueReport' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center' justifyContent={'space-between'}>
        <Stack direction='row' spacing={2} alignItems='center'>
          <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
            <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
          </IconButton>

          <Stack direction={'column'} spacing={2}>
            <BaseLabel label={t('reports_geographic_revenue_title')} styleType='bigTitle' />
            <Typography className={cx('description')}>{t('reports_geographic_revenue_desc_page')}</Typography>
          </Stack>
        </Stack>

        <Button
          type='button'
          variant='contained'
          className={cx('buttonExport')}
          onClick={handleExport}
          disabled={!geographicRevenueReport.length}
          startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
        >
          {t('common_button_export_excel', { ns: 'common/base' })}
        </Button>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable dataSource={geographicRevenueReport} columns={columns(t)} />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default GeographicRevenueReport;
